/**
 * Trims leading/trailing whitespace from free-text address and PO number fields
 * before an activity is saved, so values like "PO123 " are not stored.
 *
 * Delivery city/state/country and bill-to country are not trimmed: they come
 * from dropdowns and feed exact-match billing logic.
 */

import { ActivityData, CostItem, Invoice } from '../interfaces/activity';

const trim = <T>(value: T): T =>
    typeof value === 'string' ? (value.trim() as T) : value;

const trimInvoice = (invoice: Invoice): Invoice => ({
    ...invoice,
    po_number: trim(invoice.po_number),
    bill_to_address_1: trim(invoice.bill_to_address_1),
    bill_to_address_2: trim(invoice.bill_to_address_2),
    bill_to_city: trim(invoice.bill_to_city),
    bill_to_state: trim(invoice.bill_to_state),
    bill_to_postal_code: trim(invoice.bill_to_postal_code),
});

const trimCostItem = (costItem: CostItem): CostItem => ({
    ...costItem,
    payment_po_number_or_pcard: trim(costItem.payment_po_number_or_pcard),
});

export const trimAddressAndPoFields = (
    activity: Partial<ActivityData>,
): Partial<ActivityData> => {
    const result = { ...activity };

    // Only trim delivery fields that are in the payload. Adding a missing one
    // as undefined would blank it in the store after a partial update.
    if ('delivery_address_1' in result) {
        result.delivery_address_1 = trim(result.delivery_address_1);
    }
    if ('delivery_address_2' in result) {
        result.delivery_address_2 = trim(result.delivery_address_2);
    }
    if ('delivery_postal_code' in result) {
        result.delivery_postal_code = trim(result.delivery_postal_code);
    }
    if (result.billing_invoices) {
        result.billing_invoices = result.billing_invoices.map(trimInvoice);
    }
    if (result.cost_items) {
        result.cost_items = result.cost_items.map(trimCostItem);
    }

    return result;
};
